// @flow

/** @file Utilities for chart */

import { format } from 'date-fns';
import moment from 'moment-timezone';

import type { EstimatedBucketVolume } from '../../Usage/types';
import type { DateStr } from '../../types';
import { dateStringToDate, formatNumber } from '../../util';
import type { Data, Granularity } from './types';

/**
 * Create label for individual chart data
 *
 * @param {number} gallons - Gallons
 * @param {string} dateStr - Date string (YYYY-MM-DD)
 * @param {[number]}cost   - Cost of gallons
 * @returns {string} - Label string
 */
export const createLabel = (gallons: number, dateStr: DateStr, cost?: number): string =>
  `${formatNumber(gallons)} gallons ${cost ? `($${formatNumber(cost, 2)}) ` : ''}used on ${format(
    dateStringToDate(dateStr),
    'MMM do',
  )}`;

/** Max characters of operator reason shown in a chart tooltip before truncation. */
const TOOLTIP_REASON_MAX_LENGTH = 80;

const truncateReason = (reason: string): string =>
  reason.length > TOOLTIP_REASON_MAX_LENGTH
    ? `${reason.slice(0, TOOLTIP_REASON_MAX_LENGTH)}...`
    : reason;

/**
 * Aggregate estimated usage buckets (from the API's estimated_bucket_volumes) into
 * chart-ready data: one Datum per day/hour bucket with the volume summed across
 * same-bucket estimates, distinct reasons joined, and the tooltip label baked in
 * (the Chart renders tooltips from datum.label).
 *
 * Volumes stay in gallons here, matching the real-usage series; see the
 * EstimatedBucketVolume type for the merge-before-conversion rule.
 */
/**
 * Append the bucket's combined metered+estimated total to a tooltip label.
 * Only buckets that carry estimates get the line: without estimates the bar
 * IS the total and repeating it would be noise. Formatting matches each
 * mode's convention (daily whole gallons, hourly 2 decimals).
 */
export const appendTotalLine = (
  label: string,
  meteredVolume: number,
  estimatedVolume: number,
  granularity: Granularity,
): string => {
  if (!label || estimatedVolume <= 0) return label;
  // The total is built from the parts AS DISPLAYED (whole gallons daily, 2dp
  // hourly), not from the full-precision sum: rounding independently lets the
  // total disagree with the metered and estimated lines shown next to it
  const totalText =
    granularity === 'hourly'
      ? ((Math.round(meteredVolume * 100) + Math.round(estimatedVolume * 100)) / 100).toFixed(2)
      : formatNumber(Math.round(meteredVolume) + Math.round(estimatedVolume));
  const prefix = granularity === 'hourly' ? 'Hour total' : 'Day total';
  return `${label}\n${prefix}: ${totalText} gallons`;
};

export const buildEstimatedChartData = (
  entries: EstimatedBucketVolume[],
  granularity: Granularity,
  timezone: string,
): Data => {
  const tz = timezone || moment.tz.guess();
  const byBucket = new Map<number, { volume: number; reasons: string[] }>();

  entries.forEach((entry) => {
    const bucket = byBucket.get(entry.bucket_start) ?? { volume: 0, reasons: [] };
    bucket.volume += entry.volume;
    if (!bucket.reasons.includes(entry.reason)) {
      bucket.reasons.push(entry.reason);
    }
    byBucket.set(entry.bucket_start, bucket);
  });

  return Array.from(byBucket.entries())
    .sort(([a], [b]) => a - b)
    .map(([bucketStart, { volume, reasons }]) => {
      const epochMs = bucketStart * 1000;
      const dateStr = moment.tz(epochMs, tz).format('YYYY-MM-DD');
      const reasonText = truncateReason(reasons.join('; '));
      // Match each mode's real-usage tooltip convention: daily labels show whole
      // gallons (createLabel/formatNumber), hourly labels show 2 decimals (toFixed)
      const label =
        granularity === 'hourly'
          ? `${volume.toFixed(2)} gallons estimated at ${moment
              .tz(epochMs, tz)
              .format('M/D/YYYY h:mm A')}\nReason: ${reasonText}`
          : `${formatNumber(volume)} gallons estimated on ${format(
              dateStringToDate(dateStr),
              'MMM do',
            )}\nReason: ${reasonText}`;
      return {
        label,
        gallons: volume,
        volume,
        date: dateStr,
        ...(granularity === 'hourly' ? { xValue: epochMs } : {}),
      };
    });
};
